import type {
  Ancillary,
  AncillaryElement,
  BagCalculatorResponse,
} from "@jb-dotcom/api/bag-calculator";

// Service type constants
const SERVICE_TYPE_ENUM = {
  CARRY_ON: "B", // Carry-on items
  CHARGEABLE: "C", // Chargeable checked bags
  FREE_ALLOWANCE: "A", // Free baggage allowance
};

const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: "£",
  USD: "$",
};

const formatPrice = (
  amount: number,
  currency: string,
  includedLabel: string,
): string => {
  if (amount === 0) {
    return includedLabel;
  }
  const symbol = CURRENCY_SYMBOLS[currency] ?? currency;
  return `${symbol}${amount.toFixed(0)}`;
};

const getFreeBagAllowance = (elements: AncillaryElement[]): number => {
  const allowance = elements.find(
    (element) =>
      element.serviceType === SERVICE_TYPE_ENUM.FREE_ALLOWANCE &&
      element.maxPieces !== undefined,
  );
  return allowance?.maxPieces ? Number.parseInt(allowance.maxPieces) : 0;
};

const getCarryOnPrice = (
  elements: AncillaryElement[],
  currency: string,
  includedLabel: string,
): string => {
  const carryOn = elements.find(
    (element) =>
      element.serviceType === SERVICE_TYPE_ENUM.CARRY_ON &&
      (element.subCode === "0LN" || element.commercialName.includes("CARRYON")),
  );

  if (!carryOn) {
    return "N/A";
  }

  const amount = Number.parseFloat(carryOn.amount || "0");
  const maxPieces = Number.parseInt(carryOn.maxPieces ?? "0");

  return maxPieces > 0 && amount === 0
    ? includedLabel
    : formatPrice(amount, currency, includedLabel);
};

const getBagPrice = (
  elements: AncillaryElement[],
  bagNumber: number,
  currency: string,
  includedLabel: string,
): string => {
  // Check if bag is covered by free allowance
  const freeAllowance = getFreeBagAllowance(elements);
  if (bagNumber <= freeAllowance) {
    return includedLabel;
  }

  // Look for chargeable bag matching this occurrence
  const chargeableBag = elements.find(
    (element) =>
      element.serviceType === SERVICE_TYPE_ENUM.CHARGEABLE &&
      Number.parseInt(element.firstOccurrence ?? "0") === bagNumber,
  );

  if (!chargeableBag) {
    return "N/A";
  }

  const amount = Number.parseFloat(chargeableBag.amount || "0");
  return formatPrice(amount, currency, includedLabel);
};

const processAncillary = (
  ancillary: Ancillary | undefined,
  currency: string,
  includedLabel: string,
) => {
  const elements = ancillary?.ancillaryElement ?? [];

  return {
    carryOn: getCarryOnPrice(elements, currency, includedLabel),
    firstBag: getBagPrice(elements, 1, currency, includedLabel),
    secondBag: getBagPrice(elements, 2, currency, includedLabel),
  };
};

export const processBagFees = (
  data: BagCalculatorResponse,
  includedLabel: string,
) => {
  const bookingData = data.ancillaries.find((a) => a.requestType === "booking");
  const checkinData = data.ancillaries.find((a) => a.requestType === "checkin");

  const booking = processAncillary(bookingData, data.currency, includedLabel);
  const checkin = processAncillary(checkinData, data.currency, includedLabel);

  return {
    carryOn: {
      booking: booking.carryOn,
      checkin: checkin.carryOn,
    },
    firstBag: {
      booking: booking.firstBag,
      checkin: checkin.firstBag,
    },
    personalItem: {
      booking: includedLabel,
      checkin: includedLabel,
    },
    secondBag: {
      booking: booking.secondBag,
      checkin: checkin.secondBag,
    },
  };
};
